/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Member pages show times in the zone the viewer chose ("gym" or "device").
 * Same three input states as useGymZone; also loading until the preference is synced.
 */
import {
  DEFAULT_TIME_ZONE_DISPLAY,
  isTimeZoneDisplayPreference,
  isUsableTimeZone,
  type TimeZoneDisplayPreference,
} from "@lemonadetraining/contracts";
import { useAppSelector } from "@lemonadetraining/web-shared";
import { useGymZone, type GymZone } from "./useGymZone";

export interface DisplayZone extends GymZone {
  mode: TimeZoneDisplayPreference;
  /** The setting only means something when the gym has a zone of its own. */
  settingAvailable: boolean;
}

/** Until the saved preference is read (`synced`), the mode is only a guess, so nothing is chosen. */
export function displayZoneOf(
  gym: GymZone,
  mode: TimeZoneDisplayPreference,
  synced: boolean
): DisplayZone {
  const isLoading = gym.isLoading || !synced;
  // Same fallback as the gym zone: an unusable browser zone would make the clock helpers throw.
  const deviceZone = isUsableTimeZone(gym.browserZone) ? gym.browserZone : "UTC";
  const zone = isLoading ? null : mode === "gym" ? gym.zone : deviceZone;
  return { ...gym, isLoading, mode, zone, settingAvailable: gym.hasGymZone };
}

export function useDisplayZone(raw: string | null | undefined): DisplayZone {
  const gym = useGymZone(raw);
  // Read defensively: older stores carry no timeZoneDisplay at all.
  const stored = useAppSelector(
    (state) => (state.user as { timeZoneDisplay?: unknown }).timeZoneDisplay
  );
  // Sync sets true even when the read fails, so this never stalls.
  const synced = useAppSelector((state) => state.user.preferencesSynced === true);
  const mode = isTimeZoneDisplayPreference(stored) ? stored : DEFAULT_TIME_ZONE_DISPLAY;
  return displayZoneOf(gym, mode, synced);
}
